import { useContext } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { TabsContainer } from '@/components/TabView/Tabs';
import { ProjectDetailsContext } from './Context';
import { useProjectQuantAnalyses } from './hooks/useProjectQuantAnalyses';

type Props = {
  paths?: {
    details: string;
    entities: string;
    files: string;
    dashboard: string;
    tags: string;
    highlights: string;
    quantanalysis: string;
    summary: string;
    responses: string;
  };
};

export const ProjectTabViewContainer = ({
  paths = {
    details: `details`,
    entities: `entities`,
    files: `files`,
    dashboard: `dashboard`,
    tags: `tags`,
    highlights: `highlights`,
    quantanalysis: `quantanalysis`,
    summary: `summary`,
    responses: 'responses',
  },
}: Props) => {
  const location = useLocation();
  const quantAnalyses = useProjectQuantAnalyses();

  const ctx = useContext(ProjectDetailsContext);
  const projectId = ctx.query.data?.project?.id;

  // Generate dynamic quant analysis tabs
  const quantAnalysisTabs = quantAnalyses.map((qa) => ({
    label: qa.name,
    value: `quantanalysis/${qa.id}`,
    to: `quantanalysis/${qa.id}`,
  }));

  // Create enhanced config that includes dynamic quant analysis routes
  const enhancedConfig = [
    ...Config,
    ...quantAnalysisTabs.map((tab) => ({
      name: tab.value,
      path: `/projects/:projectId/${tab.value}`,
    })),
  ];

  const current = enhancedConfig.find((x) => matchPath(x.path, location.pathname));

  const items = [
    {
      label: 'Entities',
      value: paths.entities,
      to: 'entities',
    },
    {
      label: 'Files',
      value: paths.files,
      to: 'files',
    },
    {
      label: 'Details',
      value: paths.details,
      to: 'details',
    },
    {
      label: 'Dashboard',
      value: paths.dashboard,
      to: 'dashboard',
    },
    {
      label: 'Tags',
      value: paths.tags,
      to: 'tags',
    },
    {
      label: 'Summary',
      value: paths.summary,
      to: 'summary',
    },
    {
      label: 'Highlights',
      value: paths.highlights,
      to: 'highlights',
    },
    {
      label: 'Responses',
      value: paths.responses,
      to: 'responses',
    },
    // Add dynamic quant analysis tabs
    ...quantAnalysisTabs,
  ];

  return <TabsContainer currentTab={current?.name} items={items} />;
};

const Config = [
  {
    name: 'project',
    path: `/projects/:projectId`,
  },
  {
    name: 'details',
    path: `/projects/:projectId/details`,
  },
  {
    name: 'entities',
    path: `/projects/:projectId/entities`,
  },
  {
    name: 'files',
    path: `/projects/:projectId/files`,
  },
  {
    name: 'dashboard',
    path: `/projects/:projectId/dashboard`,
  },
  {
    name: 'tags',
    path: `/projects/:projectId/tags/:tagId?`,
  },
  {
    name: 'highlights',
    path: `/projects/:projectId/highlights`,
  },
  {
    name: 'summary',
    path: `/projects/:projectId/summary`,
  },
  {
    name: 'responses',
    path: `/projects/:projectId/responses`,
  },
];
